import React, { useState, useEffect, useCallback } from 'react';
import {
  getSedesHorarios,
  getCargosHorarios,
  crearCargo,
  actualizarPrefijoSede,
  getEmpleadosHorarios,
  actualizarPlantaSede,
  getConfiguracionGerencialApi,
  guardarConfiguracionGerencialApi
} from '../services/horariosApi';
import { resolverAlcanceABAC } from '../utils/abacHorarios';

const DIAS_SEMANA_OPCIONES = [
  { val: 1, text: 'Lunes', corto: 'L' },
  { val: 2, text: 'Martes', corto: 'M' },
  { val: 3, text: 'Miércoles', corto: 'M' },
  { val: 4, text: 'Jueves', corto: 'J' },
  { val: 5, text: 'Viernes', corto: 'V' },
  { val: 6, text: 'Sábado', corto: 'S' },
  { val: 0, text: 'Domingo', corto: 'D' }
];

const parsearDetallePlanta = (detalleTexto) => {
  if (!detalleTexto || typeof detalleTexto !== 'string') return [];
  return detalleTexto
    .split(',')
    .map(parte => parte.trim())
    .filter(Boolean)
    .map(parte => {
      const match = parte.match(/^(\d+)\s+(.+)$/);
      if (match) return { cantidad: Number(match[1]), rol: match[2].trim() };
      return { cantidad: 1, rol: parte };
    });
};

const construirTextoDetalle = (items) => {
  return items
    .filter(i => Number(i.cantidad) > 0 && String(i.rol || '').trim() !== '')
    .map(i => `${Number(i.cantidad)} ${String(i.rol).trim()}`)
    .join(', ');
};

export default function ConfiguracionAforos({ usuario, onVolver }) {
  const [sedes, setSedes] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [configHoras, setConfigHoras] = useState({});

  const [formCargo, setFormCargo] = useState({ nombre: '', prefijo: '' });
  const [editandoPrefijoId, setEditandoPrefijoId] = useState(null);
  const [nuevoPrefijoSede, setNuevoPrefijoSede] = useState('');

  const [sedeDesplegadaId, setSedeDesplegadaId] = useState(null);
  const [itemsPlantaEdit, setItemsPlantaEdit] = useState([]);
  const [cargoSeleccionadoNuevo, setCargoSeleccionadoNuevo] = useState('');
  const [cantidadNueva, setCantidadNueva] = useState(1);

  const [toast, setToast] = useState(null);

  const mostrarToast = (mensaje, tipo = 'success') => {
    setToast({ mensaje, tipo });
    setTimeout(() => setToast(null), 3800);
  };

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const [resSedes, resCargos, resEmp, resConfig] = await Promise.all([
        getSedesHorarios(),
        getCargosHorarios().catch(() => ({ success: true, data: [] })),
        getEmpleadosHorarios().catch(() => ({ success: false, data: [] })),
        getConfiguracionGerencialApi().catch(() => ({ success: false, data: null }))
      ]);

      if (resSedes && resSedes.success) setSedes(resSedes.data || []);
      
      let cargosData = [];
      if (resCargos && resCargos.success) {
        cargosData = resCargos.data || [];
        setCargos(cargosData);
        if (cargosData.length > 0) setCargoSeleccionadoNuevo(cargosData[0].nombre);
      }
      
      if (resEmp && resEmp.success) setEmpleados(resEmp.data || []);

      if (resConfig && resConfig.success && resConfig.data) {
        setConfigHoras({
          ...resConfig.data,
          porCargo: resConfig.data.porCargo || {},
          extrasMaxPorCargo: resConfig.data.extrasMaxPorCargo || {},
          sedesExtrasHabilitadas: resConfig.data.sedesExtrasHabilitadas || {},
          descansosFijosPorCargo: resConfig.data.descansosFijosPorCargo || {},
          diasBloqueadosPorSede: resConfig.data.diasBloqueadosPorSede || {}
        });
      }
    } catch (error) {
      mostrarToast('Error cargando configuración: ' + error.message, 'error');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const alcanceABAC = resolverAlcanceABAC(usuario, sedes);
  const sedesPermitidas = alcanceABAC.esGlobal ? sedes : alcanceABAC.sedesPermitidas;

  const persistirConfigGerencial = async (nuevaConfig, msg = 'Configuración guardada en Base de Datos') => {
    setConfigHoras(nuevaConfig);
    try {
      await guardarConfiguracionGerencialApi(nuevaConfig);
      mostrarToast(msg, 'success');
    } catch (error) {
      mostrarToast('Error guardando en BD: ' + error.message, 'error');
    }
  };

  const handleCambioHoraOrdinaria = (prefijo, valor) => {
    const nueva = { ...configHoras, porCargo: { ...(configHoras.porCargo || {}), [prefijo]: Number(valor) || 42 } };
    persistirConfigGerencial(nueva, `Tope ordinario de ${prefijo} actualizado en BD`);
  };

  const handleCambioExtraMax = (prefijo, valor) => {
    const nueva = { ...configHoras, extrasMaxPorCargo: { ...(configHoras.extrasMaxPorCargo || {}), [prefijo]: Math.max(0, Number(valor) || 0) } };
    persistirConfigGerencial(nueva, `Tope de horas extras de ${prefijo} actualizado en BD`);
  };

  // WFM FIX: Envía el null al backend en lugar de eliminar la llave del objeto
  const handleCambioDescansoFijo = (prefijo, diaStr) => {
    const nueva = { 
      ...configHoras, 
      descansosFijosPorCargo: { 
        ...(configHoras.descansosFijosPorCargo || {}), 
        [prefijo]: diaStr === '' ? null : Number(diaStr) 
      } 
    };
    persistirConfigGerencial(nueva, `Regla de descanso actualizada para ${prefijo}`);
  };

  const toggleDiaBloqueadoSede = (sedeId, diaNum, nombreSede) => {
    const actuales = configHoras.diasBloqueadosPorSede?.[sedeId] || [];
    const nuevos = actuales.includes(diaNum) ? actuales.filter(d => d !== diaNum) : [...actuales, diaNum].sort((a, b) => a - b);
    const nueva = { ...configHoras, diasBloqueadosPorSede: { ...(configHoras.diasBloqueadosPorSede || {}), [sedeId]: nuevos } };
    persistirConfigGerencial(nueva, `Días bloqueados en ${nombreSede} actualizados`);
  };

  const toggleExtrasSede = (prefijoSede, nombreSede) => {
    const actual = configHoras.sedesExtrasHabilitadas?.[prefijoSede] !== false;
    const nueva = { ...configHoras, sedesExtrasHabilitadas: { ...(configHoras.sedesExtrasHabilitadas || {}), [prefijoSede]: !actual } };
    persistirConfigGerencial(nueva, !actual ? `Horas extras habilitadas para ${nombreSede}` : `Horas extras bloqueadas en ${nombreSede}`);
  };

  const handleGuardarPrefijoSede = async (sedeId) => {
    const limpio = nuevoPrefijoSede.trim().toUpperCase();
    if (!limpio) return mostrarToast('El prefijo de la sede no puede estar vacío', 'error');
    try {
      await actualizarPrefijoSede(sedeId, limpio);
      setSedes(prev => prev.map(s => s.id === sedeId ? { ...s, prefijo: limpio } : s));
      setEditandoPrefijoId(null);
      mostrarToast(`Prefijo ${limpio} asignado correctamente a la sede`, 'success');
    } catch (error) { mostrarToast('Error actualizando prefijo: ' + error.message, 'error'); }
  };

  const handleCrearCargo = async (e) => {
    e.preventDefault();
    if (!formCargo.nombre.trim() || !formCargo.prefijo.trim()) return;
    try {
      await crearCargo(formCargo.nombre.trim().toUpperCase(), formCargo.prefijo.trim().toUpperCase());
      setFormCargo({ nombre: '', prefijo: '' });
      mostrarToast('Nuevo cargo oficial registrado en la base de datos', 'success');
      cargarDatos();
    } catch (error) { mostrarToast('Error creando cargo: ' + error.message, 'error'); }
  };

  const abrirDesplegableCupos = (sede) => {
    if (sedeDesplegadaId === sede.id) { setSedeDesplegadaId(null); return; }
    setSedeDesplegadaId(sede.id);
    const desgloseInicial = Array.isArray(sede.items_planta) && sede.items_planta.length > 0 ? sede.items_planta : parsearDetallePlanta(sede.detalle_planta);
    setItemsPlantaEdit(desgloseInicial.length > 0 ? desgloseInicial : [{ rol: 'Admin', cantidad: 1 }, { rol: 'Cajeros', cantidad: 5 }]);
  };

  const cambiarCantidadItem = (idx, delta) => {
    setItemsPlantaEdit(prev => prev.map((item, i) => i === idx ? { ...item, cantidad: Math.max(0, Number(item.cantidad) + delta) } : item).filter(item => item.cantidad > 0));
  };
  const cambiarRolItem = (idx, nuevoRol) => { setItemsPlantaEdit(prev => prev.map((item, i) => i === idx ? { ...item, rol: nuevoRol } : item)); };

  const agregarCargoAlDesglose = () => {
    if (!cargoSeleccionadoNuevo) return;
    const cant = Math.max(1, Number(cantidadNueva) || 1);
    setItemsPlantaEdit(prev => {
      const idxExistente = prev.findIndex(p => p.rol.toUpperCase() === cargoSeleccionadoNuevo.toUpperCase());
      if (idxExistente >= 0) return prev.map((item, i) => i === idxExistente ? { ...item, cantidad: Number(item.cantidad) + cant } : item);
      return [...prev, { rol: cargoSeleccionadoNuevo, cantidad: cant }];
    });
    setCantidadNueva(1);
  };

  const guardarCuposDeSede = async (sedeId) => {
    const totalCalculado = itemsPlantaEdit.reduce((acc, item) => acc + (Number(item.cantidad) || 0), 0);
    const detalleTexto = construirTextoDetalle(itemsPlantaEdit);
    try {
      await actualizarPlantaSede(sedeId, totalCalculado, detalleTexto);
      setSedes(prev => prev.map(s => s.id === sedeId ? { ...s, planta_autorizada: totalCalculado, detalle_planta: detalleTexto, items_planta: itemsPlantaEdit } : s));
      setSedeDesplegadaId(null);
      mostrarToast(`Planta Autorizada (${totalCalculado} cupos) guardada en MySQL`, 'success');
    } catch (error) { mostrarToast('Error guardando planta: ' + error.message, 'error'); }
  };

  const contarActivosPorSede = (sedeId) => {
    return empleados.filter(e => 
      Number(e.sede_id) === Number(sedeId) && 
      String(e.estado || 'ACTIVO').toUpperCase() === 'ACTIVO' &&
      e.codigo_interno
    ).length;
  };

  const opcionesSelectorCargos = cargos.map(c => c.nombre);

  return (
    <div className="animate-fade-in relative pb-14">
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border backdrop-blur-md transition-all ${toast.tipo === 'error' ? 'bg-red-950/90 text-white border-red-500/40' : 'bg-slate-900/95 text-white border-emerald-500/40'}`}>
          <span className={`w-2.5 h-2.5 rounded-full ${toast.tipo === 'error' ? 'bg-red-400 animate-ping' : 'bg-emerald-400'}`} />
          <span className="text-xs font-black tracking-wide">{toast.mensaje}</span>
        </div>
      )}

      <div className="flex flex-wrap justify-between items-center border-b border-gray-200 pb-4 mb-6 gap-3">
        <div>
          <h2 className="text-2xl font-black text-gray-800">⚙️ Configuración Gerencial de Horarios y Planta</h2>
          <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">Sincronización Total con Base de Datos MySQL</p>
        </div>
        <button onClick={onVolver} className="px-5 py-2.5 rounded-xl bg-white border border-gray-200 font-black text-xs text-slate-700 hover:bg-slate-100 cursor-pointer shadow-xs transition">
           Volver a Programación
        </button>
      </div>

      {cargando ? (
        <div className="py-20 text-center">
          <p className="text-gray-500 text-sm font-black animate-pulse">Sincronizando configuración gerencial desde MySQL...</p>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-sm border border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 border-b border-gray-100 pb-4">
              <div>
                <h3 className="font-black text-slate-800 text-xl">🏢 Puntos de Venta, Días Prohibidos y Planta Autorizada</h3>
                <p className="text-xs text-gray-500 font-medium mt-0.5">Bloquea días específicos donde nadie puede descansar y ajusta los cupos.</p>
              </div>
              <span className="px-3.5 py-1.5 rounded-xl bg-blue-50 text-blue-700 font-mono font-black text-xs border border-blue-200">
                {sedesPermitidas.length} Sedes Administradas
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {sedesPermitidas.map((sede) => {
                const estaDesplegada = sedeDesplegadaId === sede.id;
                const editandoPref = editandoPrefijoId === sede.id;
                const pref = sede.prefijo || 'SIN';
                const extrasActivas = configHoras.sedesExtrasHabilitadas?.[pref] !== false;
                const activosSede = contarActivosPorSede(sede.id);
                const cuposAutorizados = Number(sede.planta_autorizada || 20);
                const chipsCargos = parsearDetallePlanta(sede.detalle_planta);
                const porcentajeOcupacion = Math.min(100, Math.round((activosSede / Math.max(1, cuposAutorizados)) * 100));
                const diasBloqueados = configHoras.diasBloqueadosPorSede?.[sede.id] || [];

                return (
                  <div key={sede.id} className={`rounded-2xl border transition-all p-5 flex flex-col justify-between ${estaDesplegada ? 'border-blue-500 bg-gradient-to-b from-blue-50/40 to-white shadow-lg ring-2 ring-blue-100' : 'border-gray-200 bg-white hover:border-slate-300 shadow-xs'}`}>
                    <div>
                      <div className="flex flex-wrap justify-between items-start gap-2 mb-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-black text-slate-900 text-base tracking-tight">{sede.nombre}</h4>
                            {editandoPref ? (
                              <div className="inline-flex items-center gap-1 bg-blue-50 p-1 rounded-lg border border-blue-300">
                                <input type="text" maxLength="4" value={nuevoPrefijoSede} onChange={(e) => setNuevoPrefijoSede(e.target.value.toUpperCase())} className="w-14 px-1.5 py-0.5 text-xs font-mono font-black text-center bg-white border border-blue-400 rounded" placeholder="BAL" />
                                <button type="button" onClick={() => handleGuardarPrefijoSede(sede.id)} className="px-2 py-0.5 bg-emerald-600 text-white rounded text-[10px] font-black cursor-pointer">✓</button>
                                <button type="button" onClick={() => setEditandoPrefijoId(null)} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-[10px] font-black cursor-pointer">✕</button>
                              </div>
                            ) : (
                              <button type="button" onClick={() => { setEditandoPrefijoId(sede.id); setNuevoPrefijoSede(sede.prefijo || ''); }} title="Cambiar Prefijo ID" className="inline-flex items-center gap-1.5 font-mono text-[11px] font-black px-2.5 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 cursor-pointer transition">
                                <span>🏷️ {pref}</span><span className="text-[10px] opacity-70">✏️</span>
                              </button>
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <div className="w-28 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${activosSede > cuposAutorizados ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${porcentajeOcupacion}%` }} />
                            </div>
                            <span className="text-[10px] font-bold text-slate-500">Activos: <b className="text-slate-800">{activosSede}</b> / <b className="text-emerald-700">{cuposAutorizados}</b></span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => toggleExtrasSede(pref, sede.nombre)} className={`px-2.5 py-1 rounded-xl text-[10px] font-black cursor-pointer transition border ${extrasActivas ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'}`}>
                              {extrasActivas ? '⚡ Extras ON' : '🔒 Extras OFF'}
                            </button>
                            <span className="px-3 py-1 rounded-xl bg-slate-900 text-white font-black text-xs shadow-xs">{cuposAutorizados} Cupos</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 mb-2 p-2.5 bg-rose-50/50 border border-rose-100 rounded-xl flex items-center justify-between gap-3">
                        <span className="text-[10px] font-black text-rose-800 uppercase tracking-tight">🚫 Días Bloqueados para Descanso</span>
                        <div className="flex gap-1">
                          {DIAS_SEMANA_OPCIONES.map(dia => {
                            const bloqueado = diasBloqueados.includes(dia.val);
                            return (
                              <button key={dia.val} onClick={() => toggleDiaBloqueadoSede(sede.id, dia.val, sede.nombre)} className={`w-6 h-6 flex items-center justify-center rounded border text-[10px] font-black cursor-pointer transition ${bloqueado ? 'bg-rose-600 text-white border-rose-700 shadow-inner' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'}`} title={dia.text}>
                                {dia.corto}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {!estaDesplegada && (
                        <div className="my-3 p-3 rounded-xl bg-slate-50/80 border border-slate-100">
                          <div className="flex items-center justify-between mb-2"><span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Distribución por Cargo ({chipsCargos.length} roles)</span></div>
                          <div className="flex flex-wrap gap-1.5">
                            {chipsCargos.map((chip, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-[11px] font-bold text-slate-700 shadow-2xs">
                                <span className="px-1.5 py-0.2 rounded bg-blue-600 text-white font-black text-[10px]">{chip.cantidad}</span>
                                <span>{chip.rol}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {estaDesplegada && (
                        <div className="my-3 p-4 rounded-2xl bg-white border-2 border-blue-200 shadow-inner animate-fade-in space-y-4">
                          <div className="flex justify-between items-center border-b border-slate-100 pb-2.5">
                            <span className="text-xs font-black uppercase text-blue-900 tracking-wide">🎛️ Desglose Interactivo</span>
                            <span className="px-3 py-1 rounded-xl bg-emerald-100 text-emerald-900 font-black text-xs">Total: {itemsPlantaEdit.reduce((a, b) => a + (Number(b.cantidad) || 0), 0)}</span>
                          </div>

                          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                            {itemsPlantaEdit.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between gap-2 p-2 rounded-xl bg-slate-50 border border-slate-200/80 hover:border-blue-300 transition">
                                <select value={item.rol} onChange={(e) => cambiarRolItem(idx, e.target.value)} className="flex-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-black text-slate-800 cursor-pointer">
                                  {!opcionesSelectorCargos.includes(item.rol) && <option value={item.rol}>{item.rol}</option>}
                                  {opcionesSelectorCargos.map((op, i) => <option key={i} value={op}>{op}</option>)}
                                </select>

                                <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded-lg border border-slate-200">
                                  <button type="button" onClick={() => cambiarCantidadItem(idx, -1)} className="w-6 h-6 rounded-md bg-slate-100 hover:bg-rose-100 hover:text-rose-700 font-black text-xs cursor-pointer transition">−</button>
                                  <span className="w-7 text-center font-mono font-black text-xs text-slate-900">{item.cantidad}</span>
                                  <button type="button" onClick={() => cambiarCantidadItem(idx, 1)} className="w-6 h-6 rounded-md bg-slate-100 hover:bg-emerald-100 hover:text-emerald-700 font-black text-xs cursor-pointer transition">+</button>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="p-2.5 rounded-xl bg-blue-50/70 border border-blue-200 flex flex-wrap items-center gap-2">
                            <select value={cargoSeleccionadoNuevo} onChange={(e) => setCargoSeleccionadoNuevo(e.target.value)} className="flex-1 min-w-[140px] bg-white border border-blue-200 rounded-lg px-2.5 py-1.5 text-xs font-bold text-slate-800 cursor-pointer">
                              {opcionesSelectorCargos.map((op, i) => <option key={i} value={op}>{op}</option>)}
                            </select>
                            <input type="number" min="1" max="50" value={cantidadNueva} onChange={(e) => setCantidadNueva(e.target.value)} className="w-14 bg-white border border-blue-200 rounded-lg px-2 py-1.5 text-xs font-black text-center" />
                            <button type="button" onClick={agregarCargoAlDesglose} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-black text-xs cursor-pointer transition">+ Cargo</button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="pt-3 mt-1 border-t border-gray-100 flex justify-between items-center text-xs">
                      <span className="text-[11px] font-bold text-slate-400">Código de Sede: <b className="font-mono text-slate-700">{pref}-CARGO-01</b></span>
                      {estaDesplegada ? (
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setSedeDesplegadaId(null)} className="px-3.5 py-1.5 rounded-xl bg-gray-100 hover:bg-gray-200 font-bold text-gray-700 cursor-pointer transition">Cerrar</button>
                          <button type="button" onClick={() => guardarCuposDeSede(sede.id)} className="px-4 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black shadow-sm cursor-pointer transition">💾 Guardar</button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => abrirDesplegableCupos(sede)} className="px-4 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 font-black transition cursor-pointer flex items-center gap-1.5 border border-blue-200/60"><span>Configurar Cupos</span><span className="text-[10px]">▼</span></button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 bg-white p-6 rounded-3xl shadow-sm border border-gray-200">
              <h3 className="font-black text-slate-800 text-lg mb-1">⏱️ Matriz de Horas, Extras y Descansos por Cargo</h3>
              <p className="text-xs text-gray-500 mb-5">Configura los topes operativos y define reglas WFM dinámicas de descanso directamente desde los registros de MySQL.</p>

              <div className="overflow-x-auto max-h-[600px] custom-scrollbar">
                <table className="w-full text-left text-xs border-collapse relative">
                  <thead className="bg-slate-900 text-white uppercase font-black sticky top-0 z-10">
                    <tr>
                      <th className="p-3 rounded-tl-xl">Cargo Operativo (BD)</th>
                      <th className="p-3 text-center">Prefijo</th>
                      <th className="p-3 text-center">Tope Ordinario</th>
                      <th className="p-3 text-center">Tope Extras</th>
                      <th className="p-3 text-center bg-indigo-900">Día Descanso Fijo</th>
                      <th className="p-3 text-center rounded-tr-xl">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {cargos.length === 0 ? (
                      <tr><td colSpan="6" className="p-10 text-center font-bold text-gray-400">No hay cargos registrados en la base de datos.</td></tr>
                    ) : (
                      cargos.map(c => {
                        const ord = configHoras.porCargo?.[c.prefijo] ?? 42;
                        const ext = configHoras.extrasMaxPorCargo?.[c.prefijo] ?? 6;
                        const descansoAsignado = configHoras.descansosFijosPorCargo?.[c.prefijo] ?? '';
                        
                        return (
                          <tr key={c.prefijo} className="hover:bg-slate-50 transition-colors">
                            <td className="p-3 font-black text-slate-800 whitespace-nowrap">{c.nombre}</td>
                            <td className="p-3 text-center">
                              <span className="font-mono font-black px-2.5 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">{c.prefijo}</span>
                            </td>
                            <td className="p-3 text-center">
                              <div className="inline-flex items-center gap-1.5">
                                <input type="number" min="20" max="72" value={ord} onChange={(e) => handleCambioHoraOrdinaria(c.prefijo, e.target.value)} className="w-16 p-1.5 text-center font-black border border-gray-300 rounded-lg bg-amber-50/60" />
                                <span className="text-[10px] font-bold text-gray-500">hrs</span>
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <div className="inline-flex items-center gap-1.5">
                                <input type="number" min="0" max="24" value={ext} onChange={(e) => handleCambioExtraMax(c.prefijo, e.target.value)} className="w-16 p-1.5 text-center font-black border border-purple-300 rounded-lg bg-purple-50/60 text-purple-900" />
                                <span className="text-[10px] font-bold text-purple-700">+hrs</span>
                              </div>
                            </td>
                            <td className="p-3 text-center bg-indigo-50/30">
                              <select value={descansoAsignado} onChange={(e) => handleCambioDescansoFijo(c.prefijo, e.target.value)} className={`w-full max-w-[130px] p-1.5 border rounded-lg text-[10px] font-black cursor-pointer outline-none transition ${descansoAsignado !== '' ? 'bg-indigo-100 text-indigo-900 border-indigo-300' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                                <option value="">Rotativo (Dinámico)</option>
                                {DIAS_SEMANA_OPCIONES.map(d => <option key={d.val} value={d.val}>{d.text}</option>)}
                              </select>
                            </td>
                            <td className="p-3 text-center font-mono font-black text-slate-900 bg-slate-50/50">
                              {Number(ord) + Number(ext)}:00h
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-200 flex flex-col justify-between">
              <div>
                <h3 className="font-black text-slate-800 text-lg mb-1">💼 Catálogo de Cargos</h3>
                <p className="text-xs text-gray-500 mb-4">Crea nuevas siglas operativas (ej. <b className="font-mono">BAL-CAJ-01</b>) que se guardarán directo en MySQL.</p>

                <form onSubmit={handleCrearCargo} className="space-y-2.5 mb-5 bg-slate-50 p-3.5 rounded-2xl border border-slate-200">
                  <input type="text" required placeholder="Nombre del Cargo (Ej: DOMICILIARIO)" value={formCargo.nombre} onChange={(e) => setFormCargo({ ...formCargo, nombre: e.target.value })} className="w-full px-3 py-2 text-xs font-bold border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <div className="flex gap-2">
                    <input type="text" required maxLength="4" placeholder="Prefijo (DOM)" value={formCargo.prefijo} onChange={(e) => setFormCargo({ ...formCargo, prefijo: e.target.value.toUpperCase() })} className="flex-1 px-3 py-2 text-xs font-mono font-black border border-gray-200 rounded-xl bg-white uppercase focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl font-black text-xs transition cursor-pointer shadow-md">+ Crear Cargo</button>
                  </div>
                </form>

                <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1 custom-scrollbar">
                  {cargos.map(cargo => (
                    <div key={cargo.id} className="flex justify-between items-center p-2.5 rounded-xl bg-slate-50/70 hover:bg-slate-100 border border-slate-100 transition">
                      <span className="font-bold text-xs text-slate-700">{cargo.nombre}</span>
                      <span className="font-mono text-xs font-black px-2.5 py-0.5 rounded-lg bg-slate-800 text-white">{cargo.prefijo}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}